/* eslint complexity: ["error", 29]*/
/* eslint max-lines-per-function: ["error", 288]*/
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { pdfjs, Document, Page } from 'react-pdf';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import * as Icon2 from 'react-icons/tb';

import pointer from '../../../assets/pointer.png';
import { useTranslation } from '../../../translations';
import { useNotifications } from '../../hooks/useNotifications';
import {
  CreateUpdateContractDtoStatusEnum,
  ResponseError,
  SignaturePositionSchemaDto,
} from '../../pokCore/autogenerated/pokApiClient';

import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import './ReactPdf.scss';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const options = {
  cMapUrl: `https://unpkg.com/browse/pdfjs-dist@${pdfjs.version}/cmaps/`,
  standardFontDataUrl: `https://unpkg.com/browse/pdfjs-dist@${pdfjs.version}/standard_fonts/`,
};

const patterns = [
  'Numer zlecenia (\\d{3,11})/i',
  'Należność netto za emisje:\\s*([\\d\\s]+,\\d{2}) zł',
  'Do zapłaty:\\s*([\\d\\s]+,\\d{2})',
  'Razem do zapłaty\\s*([\\d\\s]+,\\d{2}) zł',
  'Kod zlecenia:\\s*([A-Z]+\\/\\d+\\/\\d{4})',
];
const patternToHighlight = new RegExp(patterns.join('|'), 'gi');

interface ReactPdfProps {
  file: Blob;
  withSignaturePosition?: boolean;
  setSignaturePosition: (signature: SignaturePositionSchemaDto) => void;
  signaturePosition?: SignaturePositionSchemaDto | null;
  status: CreateUpdateContractDtoStatusEnum;
  saveSignaturePosition: () => void;
  authorizedOwner: boolean;
  isPdfEncrypted: boolean;
  activeContract: boolean;
}

export const ReactPdf: React.FC<ReactPdfProps> = props => {
  const [numPages, setNumPages] = useState<number>(1);
  const [scale, setScale] = useState(1);
  const [pageNumber, setPageNumber] = useState(1);
  const [onlyImage, setOnlyImage] = useState(false);
  const [width, setWidth] = useState<number>(0);
  const [showSignaturePosition, setShowSignaturePosition] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [cursorMode, setCursorMode] = useState<'default' | 'grab'>('default');
  const startPosition = useRef<{ x: number; y: number } | null>(null);
  const scrollPosition = useRef<{ left: number; top: number } | null>(null);

  const { t, tk } = useTranslation('commonLng');
  const notifications = useNotifications();
  const signed =
    props.status === CreateUpdateContractDtoStatusEnum.Signed ||
    props.status === CreateUpdateContractDtoStatusEnum.Sent;

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setWidth(containerRef.current.clientWidth - 2);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);

    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
    setNumPages(numPages);
    setPageNumber(1);
  }

  function changePage(offset: number) {
    setPageNumber(prevPageNumber => prevPageNumber + offset);
  }

  function previousPage() {
    changePage(-1);
  }

  function nextPage() {
    changePage(1);
  }

  const changeScale = (delta: number) => {
    setScale(prevScale => Math.min(Math.max(0.5, prevScale + delta), 3.6));
  };

  const changeCursorMode = () => {
    setCursorMode(prev => (prev === 'default' ? 'grab' : 'default'));
    setShowSignaturePosition(false);
  };

  const changeSignatureMode = () => {
    setShowSignaturePosition(prev => !prev);
    setCursorMode('default');
    props.saveSignaturePosition();
  };

  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (cursorMode === 'grab' && containerRef.current) {
      setIsDragging(true);
      startPosition.current = { x: event.clientX, y: event.clientY };
      scrollPosition.current = {
        left: containerRef.current.scrollLeft,
        top: containerRef.current.scrollTop,
      };
    }
  };

  const handleMouseMove = useCallback(
    (event: MouseEvent) => {
      if (
        !isDragging ||
        !startPosition.current ||
        !scrollPosition.current ||
        !containerRef.current
      ) {
        return;
      }

      const dx = event.clientX - startPosition.current.x;
      const dy = event.clientY - startPosition.current.y;
      containerRef.current.scrollLeft = scrollPosition.current.left - dx;
      containerRef.current.scrollTop = scrollPosition.current.top - dy;
    },
    [isDragging],
  );

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', () => setIsDragging(false));
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', () => setIsDragging(false));
    };
  }, [handleMouseMove, isDragging]);

  const positionClicked = (event: React.MouseEvent<HTMLDivElement>) => {
    if (props.isPdfEncrypted) {
      return;
    }
    const pageElement = event.currentTarget;
    const rect = pageElement.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;
    props.setSignaturePosition({ x, y, page: pageNumber });
  };

  return (
    <div className="pdf-viewer pdf-viewer__scroll_x">
      {props.isPdfEncrypted && showSignaturePosition && (
        <span className="text-red ">{t(tk.attachment.encryptedPDF)}</span>
      )}
      <div className="pdf-viewer__controls">
        <div className="pdf-viewer__controls-left">
          <Button
            variant="outline-secondary"
            disabled={pageNumber <= 1}
            onClick={previousPage}
          >
            <Icon2.TbChevronLeft size={18} />
          </Button>
          <span>
            {t(tk.pdfViewer.page)} {pageNumber || (numPages ? 1 : '--')}{' '}
            {t(tk.pdfViewer.of)} {numPages || '--'}
          </span>

          <Button
            variant="outline-secondary"
            disabled={pageNumber >= numPages}
            onClick={nextPage}
          >
            <Icon2.TbChevronRight size={18} />
          </Button>
          {onlyImage && (
            <span className="text-red ">{t(tk.pdfViewer.noTextCopy)}</span>
          )}
        </div>

        <div className="pdf-viewer__controls-right">
          {props.withSignaturePosition &&
            !signed &&
            props.activeContract &&
            (props.status !==
              CreateUpdateContractDtoStatusEnum.WaitingForSigning ||
              props.authorizedOwner) && (
              <Button
                variant={
                  props.signaturePosition ? 'outline-success' : 'outline-danger'
                }
                onClick={changeSignatureMode}
                disabled={props.isPdfEncrypted && showSignaturePosition}
              >
                {`${
                  showSignaturePosition
                    ? props.isPdfEncrypted
                      ? t(tk.pdfViewer.signatureLock)
                      : t(tk.pdfViewer.confirmSignaturePointer)
                    : props.signaturePosition
                      ? t(tk.pdfViewer.changeSignaturePointer)
                      : t(tk.pdfViewer.showSignaturePointer)
                }`}
              </Button>
            )}
          <OverlayTrigger
            placement="bottom"
            overlay={
              <Tooltip>
                {cursorMode === 'default'
                  ? t(tk.pdfViewer.pan)
                  : t(tk.pdfViewer.select)}
              </Tooltip>
            }
          >
            <Button variant="outline-secondary" onClick={changeCursorMode}>
              {cursorMode === 'default' ? (
                <Icon2.TbHandStop size={18} />
              ) : (
                <Icon2.TbPointer size={18} />
              )}
            </Button>
          </OverlayTrigger>
          <Button variant="outline-secondary" onClick={() => setScale(1)}>
            <Icon2.TbZoomReset />
          </Button>
          <Button
            variant="outline-secondary"
            onClick={() => changeScale(scale < 2 ? -0.1 : -0.2)}
            disabled={scale <= 0.5}
          >
            <Icon2.TbZoomOut size={18} />
          </Button>
          <span className="pdf-viewer__scale-display">
            {Math.round(scale * 100)}%
          </span>
          <Button
            variant="outline-secondary"
            onClick={() => changeScale(scale < 2 ? 0.1 : 0.2)}
            disabled={scale >= 3.6}
          >
            <Icon2.TbZoomIn size={18} />
          </Button>
        </div>
      </div>
      <div
        className="pdf-viewer__scroll_x pdf-viewer__scroll_y"
        ref={containerRef}
        onMouseDown={handleMouseDown}
        style={{
          cursor:
            cursorMode === 'grab'
              ? isDragging
                ? 'grabbing'
                : 'grab'
              : 'default',
        }}
      >
        <Document
          file={props.file}
          onLoadSuccess={onDocumentLoadSuccess}
          onSourceError={error => {
            notifications.caughtError(error as ResponseError);
          }}
          options={options}
          loading={t(tk.pdfViewer.loading)}
          noData={t(tk.pdfViewer.noPdfFile)}
          error={t(tk.pdfViewer.error)}
        >
          <div
            className={`${showSignaturePosition && !isDragging ? 'pdf-viewer__signature' : ''}`}
            style={{
              position: 'relative',
              display: 'inline-block',
            }}
            onClick={
              showSignaturePosition && !isDragging ? positionClicked : undefined
            }
          >
            <Page
              renderTextLayer={!isDragging}
              width={width}
              pageNumber={pageNumber}
              loading={t(tk.pdfViewer.loading)}
              customTextRenderer={({ str }) => {
                return str.replace(
                  patternToHighlight,
                  value =>
                    `<mark style="border: 2px solid red; border-radius: 4px; padding: 2px 4px; color: transparent; background-color: transparent;">${value}</mark>`,
                );
              }}
              scale={scale}
              onGetTextSuccess={textContent => {
                if (textContent.items.length === 0) {
                  setOnlyImage(true);
                } else {
                  setOnlyImage(false);
                }
              }}
            />
            {!signed &&
              props.signaturePosition &&
              props.signaturePosition.page === pageNumber && (
                <img
                  src={pointer}
                  alt="Signature"
                  style={{
                    position: 'absolute',
                    left: `${props.signaturePosition.x}%`,
                    top: `${props.signaturePosition.y}%`,
                    width: '50px',
                    transform: 'translate(-50%, -50%)',
                  }}
                />
              )}
          </div>
        </Document>
      </div>
    </div>
  );
};
